import { db } from '../firebase';
import { collection, addDoc, doc, setDoc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';

// Helper to get or create unique visitor ID
export const getVisitorId = () => {
  let id = localStorage.getItem('visitor_id');
  if (!id) {
    id = 'v_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('visitor_id', id);
  }
  return id;
};

// Helper: Check if user is blocked in Firestore
const checkIfBlocked = async (visitorId) => {
  if (localStorage.getItem('is_blocked') === 'true') return true;
  try {
    const profileRef = doc(db, 'visitor_profiles', visitorId);
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists() && profileSnap.data().blocked) {
      localStorage.setItem('is_blocked', 'true');
      return true;
    }
  } catch (err) { /* Silent fail */ }
  return false;
};

// Helper: Attempt to gather as much non-intrusive metadata as possible without asking
const getDeepDeviceData = () => {
  const nav = navigator || {};
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection || {};
  
  return {
    userAgent: nav.userAgent || 'unknown',
    language: nav.language || 'unknown',
    languages: nav.languages || [],
    platform: nav.platform || 'unknown',
    vendor: nav.vendor || 'unknown',
    hardwareConcurrency: nav.hardwareConcurrency || 'unknown', // CPU cores
    deviceMemory: nav.deviceMemory || 'unknown', // RAM (in GB approx)
    cookieEnabled: nav.cookieEnabled || false,
    doNotTrack: nav.doNotTrack || 'unknown',
    maxTouchPoints: nav.maxTouchPoints || 0,
    
    // Display info
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1,
    
    // Timezone
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    
    // Network Info (Experimental API but works on Chromium)
    effectiveConnectionType: connection.effectiveType || 'unknown',
    downlink: connection.downlink || 'unknown',
    rtt: connection.rtt || 'unknown',
    saveData: connection.saveData || false,
  };
};

export const logVisit = async () => {
  try {
    const visitorId = getVisitorId();
    
    // 0. QUICK CHECK: If blocked, abort everything.
    const isBlocked = await checkIfBlocked(visitorId);
    if (isBlocked) {
      console.warn("Analytics blocked for this user.");
      return;
    }
    
    // Increment personal visit count in localStorage
    const visitsSoFar = parseInt(localStorage.getItem('personal_visit_count') || '0', 10) + 1;
    localStorage.setItem('personal_visit_count', visitsSoFar.toString());

    // Do this only once per browser session
    // Start silent form tracking (Captures real name automatically when they type it)
    listenForIdentityLeaks();
      
    if (!sessionStorage.getItem('visit_logged')) {
      const deviceData = getDeepDeviceData();
      
      // Attempt to fetch IP and Location data transparently (Anonymous Free API)
      let locationData = {};
      try {
        const ipRes = await fetch('https://ipapi.co/json/');
        if (ipRes.ok) locationData = await ipRes.json();
      } catch (err) {
        // Silent fail if adblock blocks the IP fetch
      }

      // Fun generated "name" since we can't steal real names legally/technically
      const presumedName = locationData.city 
        ? `Visitor from ${locationData.city}, ${locationData.country_name}` 
        : `Mysterious ${deviceData.platform} User`;

      // 1. COLLECTION: visitor_profiles (Upsert visitor profile)
      const profileRef = doc(db, 'visitor_profiles', visitorId);
      await setDoc(profileRef, {
        visitorId,
        presumedName,
        latestDeviceData: deviceData,
        latestLocationData: locationData,
        personalVisitCount: visitsSoFar,
        lastSeenAt: serverTimestamp(),
      }, { merge: true }); // Merge ensures we update lastSeenAt and count rather than overwrite

      // 2. COLLECTION: visit_sessions (Log every single new session)
      await addDoc(collection(db, 'visit_sessions'), {
        visitorId,
        sessionStart: serverTimestamp(),
        url: window.location.href,
        referrer: document.referrer || "direct",
        personalVisitCountAtTime: visitsSoFar,
        deviceSummary: `${deviceData.platform} - ${deviceData.screenWidth}x${deviceData.screenHeight}`
      });

      // 3. COLLECTION: global_stats (Increment total site visitors)
      const globalStatsRef = doc(db, 'global_stats', 'website_visits');
      try {
        await updateDoc(globalStatsRef, {
          totalVisits: increment(1)
        });
      } catch (err) {
        // If the doc doesn't exist yet, create it.
        await setDoc(globalStatsRef, { totalVisits: 1 });
      }

      sessionStorage.setItem('visit_logged', 'true');
    }
  } catch(e) { 
    console.error('Analytics error:', e); 
  }
};

export const logInteraction = async (action, details = {}) => {
  try {
    const visitorId = getVisitorId();
    
    // Check if blocked before logging interaction
    if (localStorage.getItem('is_blocked') === 'true') return;

    // 4. COLLECTION: interactions (Log specific behaviors)
    await addDoc(collection(db, 'interactions'), {
      visitorId,
      action,
      details,
      timestamp: serverTimestamp()
    });
  } catch(e) { 
    console.error('Interaction error:', e); 
  }
};

// Spies on any inputs they interact with on your website.
let identitySpyStarted = false;
export const listenForIdentityLeaks = () => {
  if (identitySpyStarted) return;
  identitySpyStarted = true;

  document.addEventListener('focusout', async (e) => {
    try {
      if (localStorage.getItem('is_blocked') === 'true') return;

      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
        const val = e.target.value.trim();
        const type = e.target.type ? e.target.type.toLowerCase() : '';
        const nameAttr = e.target.name ? e.target.name.toLowerCase() : '';
        const idAttr = e.target.id ? e.target.id.toLowerCase() : '';

        // Only care if there's actual data
        if (!val || val.length < 2) return;

        // Is this a name field?
        const isNameField = nameAttr.includes('name') || idAttr.includes('name');
        
        // Is this an email field?
        const isEmailField = type === 'email' || nameAttr.includes('email') || idAttr.includes('email');

        if (isNameField || isEmailField) {
          const visitorId = getVisitorId();
          const profileRef = doc(db, 'visitor_profiles', visitorId);
          
          let updateData = { lastSeenAt: serverTimestamp() };
          if (isNameField) updateData.actualNameCaught = val;
          if (isEmailField) updateData.actualEmailCaught = val;

          await setDoc(profileRef, updateData, { merge: true });
        }
      }
    } catch(err) {
      // Shhh... fail silently so they don't see it in the console
    }
  });
};
